import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api";
import { formatPagos, norm, saldoPendientePagos } from "../utils/formato";
import toast from "../utils/toast";
import { imprimirComanda } from "../utils/imprimirComanda";
import OrderForm from "../components/pos/OrderForm";
import ProductGrid from "../components/pos/ProductGrid";
import OrderSummary from "../components/pos/OrderSummary";
import MobileFooter from "../components/pos/MobileFooter";
import MobileCartSheet from "../components/pos/MobileCartSheet";
import EnvelopeSheet from "../components/pos/EnvelopeSheet";
import ConfirmPedidoModal from "../components/pos/ConfirmPedidoModal";
import CustomItemModal from "../components/pos/CustomItemModal";
import "./POS.css";

/* ===== Utils ===== */
const naturalCategorias = (a = "", b = "") => {
  const ra = /^p[aá]gina\s*(\d+)$/i.exec(a);
  const rb = /^p[aá]gina\s*(\d+)$/i.exec(b);
  if (ra && rb) return Number(ra[1]) - Number(rb[1]);
  if (ra) return -1;
  if (rb) return 1;
  return String(a).localeCompare(String(b), "es", { numeric: true, sensitivity: "base" });
};

const puedeElegirEnvoltura = (p = {}) =>
  Array.isArray(p.envolturas) && p.envolturas.length > 0;

const fechaLocal = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const isPaid = (value) => value === true || String(value).toLowerCase() === "true";

const normalizarCambiosItem = (item = {}) => {
  if (typeof item === "string") return item.replace(/\r\n/g, "\n").trim();
  if (typeof item?.cambios === "string") return item.cambios.replace(/\r\n/g, "\n").trim();
  if (Array.isArray(item?.modificaciones)) {
    return item.modificaciones.map((v) => String(v || "").trim()).filter(Boolean).join("\n");
  }
  return "";
};

export default function POS({ usuario, cajaAbierta }) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Datos del pedido
  const [pedidoEditandoId, setPedidoEditandoId] = useState(null);
  const [pedidoEstado, setPedidoEstado] = useState("nuevo");
  const [cliente, setCliente] = useState("");
  const [telefono, setTelefono] = useState("");
  const [canal, setCanal] = useState("Local");
  const [mesa, setMesa] = useState("");
  const [horaRetiro, setHoraRetiro] = useState("");
  const [medioPago, setMedioPago] = useState("");
  const [dividirPago, setDividirPago] = useState(false);
  const [medioPago2, setMedioPago2] = useState("");
  const [montoPago1Raw, setMontoPago1Raw] = useState("");
  const [pago1Pagado, setPago1Pagado] = useState(false);
  const [pago2Pagado, setPago2Pagado] = useState(false);
  const [observacion, setObservacion] = useState("");
  const [pagado, setPagado] = useState(false);
  const [trabajador, setTrabajador] = useState(false);
  const [pedidoOrigenTrabajador, setPedidoOrigenTrabajador] = useState(false);

  // Carrito
  const [pedido, setPedido] = useState([]);
  const [descuentoRaw, setDescuentoRaw] = useState(""); // "10%" o "5000"
  const [envolturaDraft, setEnvolturaDraft] = useState({});

  // Productos
  const [productos, setProductos] = useState([]);
  const [cargandoProductos, setCargandoProductos] = useState(true);
  const [errorProductos, setErrorProductos] = useState(null);
  const [filtro, setFiltro] = useState("Todo");
  const [busqueda, setBusqueda] = useState("");

  // Errores de validación del formulario
  const [erroresForm, setErroresForm] = useState({ canal: false, horaRetiro: false, medioPago: false });

  // UI
  const [numeroComanda, setNumeroComanda] = useState(1);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [guardandoBorrador, setGuardandoBorrador] = useState(false);
  const [borradores, setBorradores] = useState([]);
  const [cargandoBorradores, setCargandoBorradores] = useState(false);
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [envelopeModalProduct, setEnvelopeModalProduct] = useState(null);
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [tabletFormOpen, setTabletFormOpen] = useState(false);
  const [itemLibreOpen, setItemLibreOpen] = useState(false);
  const [cambiosModal, setCambiosModal] = useState(null);

  const limpiarPOS = useCallback(() => {
    setPedido([]);
    setCliente("");
    setTelefono("");
    setCanal("Local");
    setMesa("");
    setHoraRetiro("");
    setMedioPago("");
    setDividirPago(false);
    setMedioPago2("");
    setMontoPago1Raw("");
    setPago1Pagado(false);
    setPago2Pagado(false);
    setErroresForm({ canal: false, horaRetiro: false, medioPago: false });
    setObservacion("");
    setPagado(false);
    setTrabajador(false);
    setPedidoOrigenTrabajador(false);
    setEnvolturaDraft({});
    setDescuentoRaw("");
  }, []);

  const cargarBorradores = useCallback(async () => {
    if (!usuario?.id) {
      setBorradores([]);
      return;
    }
    try {
      setCargandoBorradores(true);
      const { data } = await api.get("/api/pedidos/borradores", { params: { user_id: usuario.id } });
      setBorradores(Array.isArray(data?.rows) ? data.rows : []);
    } catch {
      setBorradores([]);
    } finally {
      setCargandoBorradores(false);
    }
  }, [usuario?.id]);

  useEffect(() => {
    cargarBorradores();
  }, [cargarBorradores]);

  // Cargar número de comanda desde el servidor (MAX + 1)
  useEffect(() => {
    if (searchParams.get("pedido")) return;
    api.get("/api/pedidos/siguiente-comanda")
      .then(({ data }) => setNumeroComanda(data.siguiente ?? 1))
      .catch(() => setNumeroComanda(1));
  }, [searchParams]);

  const cargarPedidoExistente = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/api/pedidos/${id}`);
      const p = data?.pedido || data;
      const pagos = Array.isArray(p?.pagos) ? p.pagos : [];

      setPedidoEditandoId(Number(id));
      setPedidoEstado(p?.estado || "confirmado");
      setCliente(p?.cliente || "");
      setTelefono(p?.telefono || "");
      setCanal(p?.canal || "Local");
      setMesa(p?.mesa || "");
      setHoraRetiro(p?.hora_retiro || "");
      setObservacion(p?.observacion || "");
      const cargadoTrabajador = p?.trabajador === true || String(p?.trabajador).toLowerCase() === "true";
      const cargadoTrabajadorConvertido = p?.trabajador_convertido === true || String(p?.trabajador_convertido).toLowerCase() === "true";
      setTrabajador(cargadoTrabajador);
      setPedidoOrigenTrabajador(cargadoTrabajador || cargadoTrabajadorConvertido);
      setNumeroComanda(p?.numero_comanda ?? 1);
      setDescuentoRaw(Number(p?.descuento || 0) > 0 ? String(Math.round(Number(p.descuento))) : "");
      if (pagos.length > 1) {
        setDividirPago(true);
        setMedioPago(pagos[0]?.medio || p?.medio_pago || "");
        setMedioPago2(pagos[1]?.medio || "");
        setMontoPago1Raw(String(Math.round(Number(pagos[0]?.monto || 0))));
        setPago1Pagado(isPaid(pagos[0]?.pagado));
        setPago2Pagado(isPaid(pagos[1]?.pagado));
        setPagado(isPaid(p?.pagado));
      } else {
        setDividirPago(false);
        setMedioPago(pagos[0]?.medio || p?.medio_pago || "");
        setMedioPago2("");
        setMontoPago1Raw("");
        setPago1Pagado(false);
        setPago2Pagado(false);
        setPagado(pagos.length ? isPaid(pagos[0]?.pagado) : isPaid(p?.pagado));
      }

      setPedido((p?.items || []).map((it) => ({
        producto_id: it.producto_id ?? `libre-${it.id || Date.now()}`,
        nombre: it.nombre || "",
        precio: Number(it.precio || 0),
        cantidad: Number(it.cantidad || 1),
        envoltura: it.envoltura || null,
        cambios: normalizarCambiosItem(it),
      })));
      setErroresForm({ canal: false, horaRetiro: false, medioPago: false });
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || "No se pudo cargar el pedido";
      toast(msg);
      setPedidoEditandoId(null);
      setSearchParams({}, { replace: true });
    }
  }, [setSearchParams]);

  useEffect(() => {
    const pedidoId = searchParams.get("pedido");
    if (!pedidoId) {
      setPedidoEditandoId(null);
      setPedidoEstado("nuevo");
      return;
    }
    if (!/^\d+$/.test(pedidoId)) {
      toast("Pedido invalido.");
      setSearchParams({}, { replace: true });
      return;
    }
    cargarPedidoExistente(pedidoId);
  }, [searchParams, cargarPedidoExistente, setSearchParams]);

  useEffect(() => {
    if (!trabajador) return;
    setMedioPago("");
    setDividirPago(false);
    setMedioPago2("");
    setMontoPago1Raw("");
    setPago1Pagado(false);
    setPago2Pagado(false);
    setPagado(false);
    setErroresForm((e) => ({ ...e, medioPago: false }));
  }, [trabajador]);

  // Detectar layout móvil
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 520px)");
    const handler = () => setIsMobileLayout(mq.matches);
    handler();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Cargar productos activos
  useEffect(() => {
    (async () => {
      try {
        setCargandoProductos(true);
        const { data } = await api.get("/api/productos?activos=1");
        const raw = Array.isArray(data) ? data : [];
        setProductos(
          raw.filter(Boolean).map((p) => ({
            ...p,
            id: p.id != null ? p.id : undefined,
            precio: Number(p.precio) || 0,
            categoria: p.categoria || "Sin categoría",
            envolturas: Array.isArray(p.envolturas) ? p.envolturas : [],
          }))
        );
        setErrorProductos(null);
      } catch (e) {
        const msg = e?.response?.data?.error ?? e?.message ?? "Error cargando productos";
        setErrorProductos(typeof msg === "string" ? msg : "Error cargando productos");
      } finally {
        setCargandoProductos(false);
      }
    })();
  }, []);

  // Categorías y filtrado
  const categorias = useMemo(() => {
    const list = Array.isArray(productos) ? productos : [];
    const cats = Array.from(
      new Set(list.map((p) => (p && p.categoria) || "Sin categoría"))
    ).sort(naturalCategorias);
    return ["Todo", ...cats];
  }, [productos]);

  useEffect(() => {
    if (filtro !== "Todo" && !categorias.includes(filtro)) setFiltro("Todo");
  }, [categorias, filtro]);

  const byCategoria = useMemo(() => {
    const list = Array.isArray(productos) ? productos : [];
    if (filtro === "Todo") return list.slice();
    return list.filter((p) => (p && (p.categoria || "Sin categoría")) === filtro);
  }, [productos, filtro]);

  const menuFiltrado = useMemo(() => {
    const base = Array.isArray(byCategoria) ? byCategoria : [];
    const q = norm(busqueda);
    const list = q ? base.filter((p) => p && norm(p.nombre || "").includes(q)) : base;
    return [...list].sort((a, b) => {
      if (!a || !b) return 0;
      const pa = /^p[aá]gina\s*(\d+)$/i.exec(a.categoria || "");
      const pb = /^p[aá]gina\s*(\d+)$/i.exec(b.categoria || "");
      if (pa && pb) return Number(pa[1]) - Number(pb[1]);
      if (pa) return -1;
      if (pb) return 1;
      return (a.nombre || "").localeCompare(b.nombre || "", "es", { numeric: true, sensitivity: "base" });
    });
  }, [byCategoria, busqueda]);

  // Totales
  const subtotal = useMemo(
    () => (pedido ?? []).reduce((acc, it) => acc + Number(it.precio || 0) * Number(it.cantidad || 0), 0),
    [pedido]
  );
  const descuento = useMemo(() => {
    const raw = String(descuentoRaw).trim();
    if (!raw) return 0;
    if (raw.endsWith("%")) {
      const pct = Number(raw.slice(0, -1).replace(",", "."));
      if (!Number.isFinite(pct) || pct < 0) return 0;
      return Math.round((subtotal * pct) / 100);
    }
    const val = Number(raw.replace(/\./g, "").replace(",", "."));
    return Number.isFinite(val) && val >= 0 ? Math.round(val) : 0;
  }, [subtotal, descuentoRaw]);
  const totalFinal = useMemo(() => Math.max(0, subtotal - descuento), [subtotal, descuento]);
  const montoPago1 = useMemo(() => {
    const raw = String(montoPago1Raw).replace(/\./g, "").replace(",", ".");
    const n = Number(raw);
    return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
  }, [montoPago1Raw]);
  const pagosPedido = useMemo(() => {
    if (trabajador) return [];
    if (!medioPago) return [];
    if (!dividirPago) return [{ medio: medioPago, monto: totalFinal, pagado }];
    const monto1 = Math.min(totalFinal, montoPago1);
    const monto2 = Math.max(0, totalFinal - monto1);
    return [
      { medio: medioPago, monto: monto1, pagado: pago1Pagado },
      { medio: medioPago2, monto: monto2, pagado: pago2Pagado },
    ].filter((p) => p.medio);
  }, [dividirPago, medioPago, medioPago2, montoPago1, pago1Pagado, pago2Pagado, pagado, totalFinal, trabajador]);
  const pagoDisplay = useMemo(() => trabajador ? "Trabajador" : formatPagos(pagosPedido, medioPago), [pagosPedido, medioPago, trabajador]);
  const pagoComanda = useMemo(() => trabajador ? "TRABAJADOR" : (dividirPago ? pagoDisplay : medioPago), [dividirPago, medioPago, pagoDisplay, trabajador]);
  const pagadoPedido = useMemo(() => !trabajador && pagosPedido.length > 0 && pagosPedido.every((p) => isPaid(p.pagado)), [pagosPedido, trabajador]);
  const saldoPendiente = useMemo(() => trabajador ? 0 : saldoPendientePagos(pagosPedido), [pagosPedido, trabajador]);

  // Acciones del carrito
  const agregarItem = (item, envelopeOverride = undefined) => {
    const wrap =
      envelopeOverride !== undefined
        ? envelopeOverride
        : puedeElegirEnvoltura(item) && envolturaDraft[item.id]
          ? envolturaDraft[item.id]
          : "";
    const nombreConWrap = wrap ? `${item.nombre} – ${wrap}` : item.nombre;
    const existe = pedido.find((i) => i.producto_id === item.id && i.nombre === nombreConWrap);
    if (existe) {
      setPedido(pedido.map((i) =>
        i.producto_id === item.id && i.nombre === nombreConWrap
          ? { ...i, cantidad: i.cantidad + 1 }
          : i
      ));
    } else {
      setPedido([...pedido, {
        producto_id: item.id,
        nombre: nombreConWrap,
        envoltura: wrap || null,
        precio: Number(item.precio),
        cantidad: 1,
        cambios: "",
      }]);
    }
  };

  const agregarItemLibre = ({ nombre, precio, cantidad }) => {
    setPedido((prev) => [
      ...prev,
      {
        producto_id: `libre-${Date.now()}`,
        nombre,
        precio: Number(precio),
        cantidad: Number(cantidad),
        envoltura: null,
        cambios: "",
      },
    ]);
  };

  const editarCambiosItem = (producto_id, nombre) => {
    const actual = pedido.find((i) => i.producto_id === producto_id && i.nombre === nombre);
    if (!actual) return;
    setCambiosModal({
      producto_id,
      nombre,
      cambios: normalizarCambiosItem(actual),
    });
  };

  const guardarCambiosItem = () => {
    if (!cambiosModal) return;
    const cambios = normalizarCambiosItem(cambiosModal.cambios);
    setPedido((prev) => prev.map((i) =>
      i.producto_id === cambiosModal.producto_id && i.nombre === cambiosModal.nombre
        ? { ...i, cambios }
        : i
    ));
    setCambiosModal(null);
  };

  const eliminarItem = (producto_id, nombre) => {
    setPedido(
      (pedido ?? [])
        .map((i) =>
          i.producto_id === producto_id && (nombre ? i.nombre === nombre : true)
            ? { ...i, cantidad: i.cantidad - 1 }
            : i
        )
        .filter((i) => i.cantidad > 0)
    );
  };


  const configurarDescuento = () => {
    const txt = window.prompt(
      "Ingresa el descuento (monto en pesos o porcentaje, ej: 1500 o 10%):",
      descuentoRaw || ""
    );
    if (txt === null) return;
    const raw = String(txt).trim();
    if (!raw) { setDescuentoRaw(""); return; }

    // Validar antes de guardar
    if (raw.endsWith("%")) {
      const pct = Number(raw.slice(0, -1).replace(",", "."));
      if (!Number.isFinite(pct) || pct < 0) { toast("Porcentaje inválido."); return; }
    } else {
      const val = Number(raw.replace(/\./g, "").replace(",", "."));
      if (!Number.isFinite(val) || val < 0) { toast("Monto inválido."); return; }
    }
    setDescuentoRaw(raw);
  };

  const triggerImprimir = (numeroComandaOverride = numeroComanda) => {
    imprimirComanda({
      numeroComanda: numeroComandaOverride,
      cliente,
      telefono,
      canal,
      horaRetiro,
      medioPago: pagoComanda,
      pagos: pagosPedido,
      pagado: pagadoPedido,
      observacion,
      pedido,
      subtotal,
      descuento,
      totalFinal,
      trabajador,
    });
  };

  const buildPedidoPayload = () => {
    const items = (pedido || []).map((p) => ({
      producto_id: Number.isFinite(Number(p.producto_id)) ? Number(p.producto_id) : null,
      nombre: String(p.nombre || "").trim(),
      cantidad: Math.max(1, Number(p.cantidad || 1)),
      precio: Math.round(Number(p.precio || 0)),
      cambios: normalizarCambiosItem(p),
    }));
    return {
      cliente: cliente || null,
      telefono: telefono || null,
      canal: canal || null,
      mesa: mesa || null,
      fecha_retiro: fechaLocal(),
      hora_retiro: horaRetiro || null,
      items,
      subtotal: Number(subtotal || 0),
      descuento: Math.max(0, Number(descuento || 0)),
      total: Number(totalFinal || 0),
      medio_pago: trabajador ? null : (medioPago || null),
      pagos: trabajador ? [] : pagosPedido,
      observacion: observacion || null,
      pagado: trabajador ? false : pagadoPedido,
      numero_comanda: pedidoEstado === "borrador" ? null : (numeroComanda ?? null),
      user_id: usuario?.id ?? null,
      cuenta_personal: false,
      trabajador,
      trabajador_convertido: !trabajador && pedidoOrigenTrabajador,
      origen_trabajador: pedidoOrigenTrabajador,
    };
  };

  const guardarBorrador = async () => {
    if ((pedido ?? []).length === 0) return toast("El pedido está vacío.");
    if (pedidoEditandoId && pedidoEstado !== "borrador") {
      return toast("Este pedido ya está confirmado. Puedes actualizarlo con Enviar Pedido.");
    }

    try {
      setGuardandoBorrador(true);
      const payload = buildPedidoPayload();
      if (pedidoEditandoId && pedidoEstado === "borrador") {
        await api.put(`/api/pedidos/${pedidoEditandoId}/borrador`, payload);
        toast("Borrador actualizado.");
      } else {
        await api.post("/api/pedidos/borradores", payload);
        toast("Borrador guardado.");
      }
      limpiarPOS();
      setPedidoEditandoId(null);
      setPedidoEstado("nuevo");
      if (searchParams.get("pedido")) {
        setSearchParams({}, { replace: true });
      }
      await cargarBorradores();
    } catch (error) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || error?.message || "No se pudo guardar el borrador";
      toast(msg);
    } finally {
      setGuardandoBorrador(false);
    }
  };

  const eliminarBorrador = async (id) => {
    try {
      await api.delete(`/api/pedidos/${id}`);
      if (Number(pedidoEditandoId) === Number(id)) {
        limpiarPOS();
        setPedidoEditandoId(null);
        setPedidoEstado("nuevo");
        setSearchParams({}, { replace: true });
      }
      await cargarBorradores();
      toast("Borrador eliminado.");
    } catch (error) {
      toast(error?.response?.data?.error || "No se pudo eliminar el borrador");
    }
  };

  const confirmarEnvio = async () => {
    try {
      setEnviando(true);
      const payload = buildPedidoPayload();
      let numeroComandaFinal = numeroComanda;

      if (pedidoEditandoId) {
        const { data } = await api.put(`/api/pedidos/${pedidoEditandoId}`, payload);
        numeroComandaFinal = data?.numero_comanda ?? numeroComandaFinal;
      } else {
        await api.post("/api/pedidos/crear", payload);
      }

      triggerImprimir(numeroComandaFinal);
      limpiarPOS();
      setPedidoEditandoId(null);
      setPedidoEstado("nuevo");
      if (searchParams.get("pedido")) {
        setSearchParams({}, { replace: true });
      }
      cargarBorradores();
      // Obtener el siguiente número de comanda desde el servidor
      api.get("/api/pedidos/siguiente-comanda")
        .then(({ data }) => setNumeroComanda(data.siguiente ?? numeroComanda + 1))
        .catch(() => setNumeroComanda((n) => n + 1));
      setMostrarModal(false);
    } catch (error) {
      console.error(error);
      const msg = error?.response?.data?.error || error?.response?.data?.message || error?.message || "Error al enviar pedido";
      toast(`Error al enviar pedido: ${msg}`);
    } finally {
      setEnviando(false);
    }
  };

  const enviarPedido = () => {
    if (!cliente) return toast("Debes ingresar el nombre del cliente.");
    if ((pedido ?? []).length === 0) return toast("El pedido está vacío.");
    if (cajaAbierta === false) return toast("Debe iniciar la caja antes de registrar pedidos.");

    const nuevosErrores = {
      canal:      !canal,
      horaRetiro: !horaRetiro,
      medioPago:  !trabajador && (!medioPago || (dividirPago && (!medioPago2 || montoPago1 <= 0 || montoPago1 >= totalFinal || medioPago2 === medioPago))),
    };
    setErroresForm(nuevosErrores);

    if (Object.values(nuevosErrores).some(Boolean)) {
      const faltantes = [
        nuevosErrores.canal      && "Canal",
        nuevosErrores.horaRetiro && "Hora de Retiro",
        nuevosErrores.medioPago  && "Medios de Pago",
      ].filter(Boolean).join(", ");
      return toast(`Completá los campos requeridos: ${faltantes}`);
    }

    setMostrarModal(true);
  };

  return (
    <div className="pos-page">
      <header className="pos-header pos-header--compact">
        <div className="ticket">
          <span>N° Comanda</span>
          <strong>{numeroComanda}</strong>
        </div>
      </header>

      {cajaAbierta === false && (
        <div className="pos-aviso-caja" role="alert">
          <span className="pos-aviso-caja__icon" aria-hidden>⚠</span>
          <span>
            Debe iniciar la caja para poder registrar pedidos. Use el botón &quot;Iniciar caja&quot; arriba.
          </span>
        </div>
      )}

      {pedidoEditandoId && (
        <div className="pos-editing-banner" role="status">
          {pedidoEstado === "borrador"
            ? "Editando borrador. Al enviar se registrara como pedido y se imprimira la comanda."
            : `Editando comanda #${numeroComanda}. Al confirmar se actualizara y se reimprimira la comanda completa.`}
        </div>
      )}

      <section className="pos-drafts" aria-label="Borradores pendientes">
        <div className="pos-drafts__head">
          <strong>Borradores</strong>
          <span>{cargandoBorradores ? "Cargando..." : `${borradores.length} pendiente${borradores.length === 1 ? "" : "s"}`}</span>
        </div>
        {borradores.length > 0 && (
          <div className="pos-drafts__list">
            {borradores.map((b) => (
              <article className="pos-draft" key={b.id}>
                <div className="pos-draft__main">
                  <strong>{b.cliente || "Sin nombre"}</strong>
                  <span>{(b.items || []).length} item{(b.items || []).length === 1 ? "" : "s"} · {b.hora_retiro || "Sin hora"}</span>
                </div>
                <div className="pos-draft__actions">
                  <button type="button" className="btn btn-sm" onClick={() => setSearchParams({ pedido: String(b.id) })}>
                    Abrir
                  </button>
                  <button type="button" className="btn btn-sm danger" onClick={() => eliminarBorrador(b.id)}>
                    Eliminar
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <div className="pos-board">
        <section className="pos-3col">
          <OrderForm
            cliente={cliente} setCliente={setCliente}
            telefono={telefono} setTelefono={setTelefono}
            canal={canal} setCanal={(v) => { setCanal(v); setErroresForm((e) => ({ ...e, canal: false })); }}
            mesa={mesa} setMesa={setMesa}
            horaRetiro={horaRetiro} setHoraRetiro={(v) => { setHoraRetiro(v); setErroresForm((e) => ({ ...e, horaRetiro: false })); }}
            medioPago={medioPago} setMedioPago={(v) => { setMedioPago(v); setErroresForm((e) => ({ ...e, medioPago: false })); }}
            trabajador={trabajador} setTrabajador={setTrabajador}
            dividirPago={dividirPago} setDividirPago={setDividirPago}
            medioPago2={medioPago2} setMedioPago2={(v) => { setMedioPago2(v); setErroresForm((e) => ({ ...e, medioPago: false })); }}
            montoPago1Raw={montoPago1Raw} setMontoPago1Raw={(v) => { setMontoPago1Raw(v); setErroresForm((e) => ({ ...e, medioPago: false })); }}
            pago1Pagado={pago1Pagado} setPago1Pagado={setPago1Pagado}
            pago2Pagado={pago2Pagado} setPago2Pagado={setPago2Pagado}
            totalFinal={totalFinal}
            observacion={observacion} setObservacion={setObservacion}
            pagado={pagado} setPagado={setPagado}
            mobileDetailsOpen={mobileDetailsOpen}
            setMobileDetailsOpen={setMobileDetailsOpen}
            erroresForm={erroresForm}
          />

          <ProductGrid
            filtro={filtro} setFiltro={setFiltro}
            categorias={categorias}
            busqueda={busqueda} setBusqueda={setBusqueda}
            menuFiltrado={menuFiltrado}
            cargandoProductos={cargandoProductos}
            errorProductos={errorProductos}
            envolturaDraft={envolturaDraft}
            setEnvolturaDraft={setEnvolturaDraft}
            enviando={enviando}
            isMobileLayout={isMobileLayout}
            cajaAbierta={cajaAbierta}
            onAgregarItem={agregarItem}
            onOpenEnvelope={setEnvelopeModalProduct}
            onOpenItemLibre={() => setItemLibreOpen(true)}
          />

          <OrderSummary
            pedido={pedido}
            subtotal={subtotal}
            totalFinal={totalFinal}
            descuento={descuento}
            onConfigureDescuento={configurarDescuento}
            onEliminarItem={eliminarItem}
            onEditarCambios={editarCambiosItem}
            onEnviarPedido={enviarPedido}
            onGuardarBorrador={guardarBorrador}
            horaRetiro={horaRetiro}
            medioPago={pagoDisplay}
            pagoDisplay={pagoDisplay}
            saldoPendiente={saldoPendiente}
            pagado={pagadoPedido}
            observacion={observacion}
            enviando={enviando}
            guardandoBorrador={guardandoBorrador}
            cajaAbierta={cajaAbierta}
            onOpenTabletForm={() => setTabletFormOpen(true)}
            trabajador={trabajador}
          />
        </section>
      </div>

      <MobileFooter
        pedido={pedido}
        totalFinal={totalFinal}
        enviando={enviando}
        cajaAbierta={cajaAbierta}
        onOpenCart={() => setMobileCartOpen(true)}
        onConfigureDescuento={configurarDescuento}
        onEnviarPedido={enviarPedido}
        onGuardarBorrador={guardarBorrador}
      />

      <MobileCartSheet
        open={mobileCartOpen}
        onClose={() => setMobileCartOpen(false)}
        pedido={pedido}
        subtotal={subtotal}
        totalFinal={totalFinal}
        descuento={descuento}
        onEliminarItem={eliminarItem}
        onEditarCambios={editarCambiosItem}
        enviando={enviando}
      />

      <EnvelopeSheet
        product={envelopeModalProduct}
        onSelect={agregarItem}
        onClose={() => setEnvelopeModalProduct(null)}
        enviando={enviando}
        cargandoProductos={cargandoProductos}
      />

      <CustomItemModal
        open={itemLibreOpen}
        onClose={() => setItemLibreOpen(false)}
        onAgregar={agregarItemLibre}
      />

      {cambiosModal && (
        <div className="modal cambios-modal" onClick={() => setCambiosModal(null)}>
          <div className="modal__dialog cambios-modal__dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal__head">
              <div>
                <h3 className="modal__title">Cambios del producto</h3>
                <p className="cambios-modal__product">{cambiosModal.nombre}</p>
              </div>
              <button
                className="modal__close"
                onClick={() => setCambiosModal(null)}
                aria-label="Cerrar"
                title="Cerrar"
              >
                ×
              </button>
            </div>
            <textarea
              className="cambios-modal__textarea"
              value={cambiosModal.cambios}
              onChange={(e) => setCambiosModal((m) => ({ ...m, cambios: e.target.value }))}
              placeholder={"Ej:\nSin wasabi\nCambiar 10 piezas a fritas\nSin queso, con palta"}
              rows={7}
              autoFocus
            />
            <div className="modal__actions">
              <button className="btn" type="button" onClick={() => setCambiosModal(null)}>
                Cancelar
              </button>
              <button className="btn btn-accent" type="button" onClick={guardarCambiosItem}>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {tabletFormOpen && (
        <div
          className="tablet-form-modal"
          onClick={() => setTabletFormOpen(false)}
        >
          <div
            className="tablet-form-modal__panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tablet-form-modal__head">
              <h3 className="tablet-form-modal__title">Datos del pedido</h3>
              <button
                type="button"
                className="tablet-form-modal__close"
                onClick={() => setTabletFormOpen(false)}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="tablet-form-modal__body">
              <OrderForm
                cliente={cliente} setCliente={setCliente}
                telefono={telefono} setTelefono={setTelefono}
                canal={canal} setCanal={(v) => { setCanal(v); setErroresForm((e) => ({ ...e, canal: false })); }}
                mesa={mesa} setMesa={setMesa}
                horaRetiro={horaRetiro} setHoraRetiro={(v) => { setHoraRetiro(v); setErroresForm((e) => ({ ...e, horaRetiro: false })); }}
                medioPago={medioPago} setMedioPago={(v) => { setMedioPago(v); setErroresForm((e) => ({ ...e, medioPago: false })); }}
                trabajador={trabajador} setTrabajador={setTrabajador}
                dividirPago={dividirPago} setDividirPago={setDividirPago}
                medioPago2={medioPago2} setMedioPago2={(v) => { setMedioPago2(v); setErroresForm((e) => ({ ...e, medioPago: false })); }}
                montoPago1Raw={montoPago1Raw} setMontoPago1Raw={(v) => { setMontoPago1Raw(v); setErroresForm((e) => ({ ...e, medioPago: false })); }}
                pago1Pagado={pago1Pagado} setPago1Pagado={setPago1Pagado}
                pago2Pagado={pago2Pagado} setPago2Pagado={setPago2Pagado}
                totalFinal={totalFinal}
                observacion={observacion} setObservacion={setObservacion}
                pagado={pagado} setPagado={setPagado}
                mobileDetailsOpen={true}
                setMobileDetailsOpen={() => {}}
                erroresForm={erroresForm}
              />
            </div>
            <div className="tablet-form-modal__footer">
              <button
                type="button"
                className="tablet-form-modal__done"
                onClick={() => setTabletFormOpen(false)}
              >
                Listo
              </button>
            </div>
          </div>
        </div>
      )}

      {mostrarModal && (
        <ConfirmPedidoModal
          pedido={pedido}
          subtotal={subtotal}
          totalFinal={totalFinal}
          descuento={descuento}
          cliente={cliente}
          telefono={telefono}
          canal={canal}
          horaRetiro={horaRetiro}
          medioPago={medioPago}
          pagos={pagosPedido}
          pagoDisplay={pagoDisplay}
          saldoPendiente={saldoPendiente}
          pagado={pagadoPedido}
          observacion={observacion}
          trabajador={trabajador}
          enviando={enviando}
          onConfirmar={confirmarEnvio}
          onCancelar={() => setMostrarModal(false)}
        />
      )}
    </div>
  );
}
